import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Plus, GripVertical, X, User, ChevronDown, ChevronRight } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { useModels } from '../../../lib/supabase/models/supabaseModelService';
import { useUsers } from '../../../lib/supabase/users/supabaseUserService';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  useAllAssignments, 
  useAssignModel, 
  useRemoveModel, 
  useMoveModel,
  useAllUserOrders,
  useUpdateUserOrders,
  useUpdateAssignmentOrder,
  FanGainAssignment
} from '../../../lib/supabase/fan-gain/supabaseUserModelAssignmentService';
import { LoadingState } from '../../ui/loading-state';
import { toast } from 'react-hot-toast';
import { Model } from '@shiftly/shared/src/schema';
import { GG_SWAP_ROLE } from '@shiftly/shared/src/constants';

interface ModelCardProps {
  model: Model;
  onRemove: (model_id: string) => void;
  canManage: boolean;
  user_id: string;
  index: number;
  onReorderModels: (user_id: string, draggedIndex: number, targetIndex: number) => void;
}

const ModelCard: React.FC<ModelCardProps> = ({ model, onRemove, canManage, user_id, index, onReorderModels }) => {
  return (
    <div 
      className="group flex items-center justify-between bg-white dark:bg-brand-dark border border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 shadow-sm hover:shadow-md transition-all cursor-move"
      draggable={canManage}
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData('application/json', JSON.stringify({
          type: 'model',
          model_id: model.id,
          modelName: model.name,
          sourceuser_id: user_id,
          sourceIndex: index
        }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!canManage) return;

        try {
          const data = JSON.parse(e.dataTransfer.getData('application/json'));
          if (data.type === 'model' && data.sourceuser_id === user_id && data.sourceIndex !== index) {
            onReorderModels(user_id, data.sourceIndex, index);
          }
        } catch (error) {
          console.error('Error handling model drop:', error);
        }
      }}
    >
      <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
        {canManage && <GripVertical className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />}
        <span className="font-medium text-xs sm:text-sm truncate">{model.name}</span>
      </div>
      {canManage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(model.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 sm:h-6 sm:w-6 p-0 text-brand-red hover:text-brand-red flex-shrink-0"
        >
          <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
        </Button>
      )}
    </div>
  );
};

interface AddModelSlotProps {
  user_id: string;
  availableModels: Model[];
  onAddModel: (model_id: string, user_id: string) => void;
  canManage: boolean;
}

const AddModelSlot: React.FC<AddModelSlotProps> = ({ user_id, availableModels, onAddModel, canManage }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!canManage) {
    return (
      <div className="border-2 border-dashed border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 min-h-[40px] sm:min-h-[46px] flex items-center justify-center text-muted-foreground dark:text-muted-foreground">
        <span className="text-xs">Drop here</span>
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="w-full border-2 border-dashed border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 min-h-[40px] sm:min-h-[46px] flex items-center justify-center text-muted-foreground dark:text-muted-foreground hover:border-brand-border dark:hover:border-brand-border hover:text-muted-foreground dark:hover:text-muted-foreground transition-colors">
          <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          <span className="text-xs">Add Model</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search models..." className="text-sm" />
          <CommandList>
            <CommandEmpty>No available models found.</CommandEmpty>
            <CommandGroup>
              {availableModels.map(model => (
                <CommandItem 
                  key={model.id}
                  onSelect={() => {
                    onAddModel(model.id, user_id);
                    setIsOpen(false);
                  }}
                  className="text-sm"
                >
                  {model.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface UserCardProps {
  user: { id: string; full_name: string };
  assignedModels: Model[];
  availableModels: Model[];
  onAddModel: (model_id: string, user_id: string) => void;
  onRemoveModel: (model_id: string, user_id: string) => void;
  onDrop: (e: React.DragEvent, user_id: string) => void;
  onReorderModels: (user_id: string, draggedIndex: number, targetIndex: number) => void;
  canManage: boolean;
  index: number;
  onReorderUsers: (draggedIndex: number, targetIndex: number) => void;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  assignedModels,
  availableModels,
  onAddModel,
  onRemoveModel,
  onDrop,
  onReorderModels,
  canManage,
  index,
  onReorderUsers
}) => {
  return (
    <Card 
      draggable={canManage}
      onDrop={(e) => {
        e.stopPropagation();
        try {
          const data = JSON.parse(e.dataTransfer.getData('application/json'));
          if (data.type === 'user' && data.sourceIndex !== index) {
            onReorderUsers(data.sourceIndex, index);
          } else if (data.type === 'model') {
            onDrop(e, user.id);
          }
        } catch (error) {
          console.error('Error handling drop:', error);
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDragStart={(e) => {
        if (!canManage) return;
        // Only allow user dragging if not dragging from within the card content
        const target = e.target as HTMLElement;
        const isFromCardContent = target.closest('.user-card-content');
        if (isFromCardContent) {
          e.preventDefault();
          return;
        }
        
        e.dataTransfer.setData('application/json', JSON.stringify({
          type: 'user',
          user_id: user.id,
          sourceIndex: index
        }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      className="cursor-move"
    >
      <CardHeader className="pb-2 sm:pb-3">
        <CardTitle className="text-sm sm:text-base flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>{user.full_name}</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 user-card-content">
        {assignedModels.map((model, modelIndex) => (
          <ModelCard
            key={model.id}
            model={model}
            onRemove={(model_id) => onRemoveModel(model_id, user.id)}
            canManage={canManage}
            user_id={user.id}
            index={modelIndex}
            onReorderModels={onReorderModels}
          />
        ))}
        <AddModelSlot 
          user_id={user.id}
          availableModels={availableModels}
          onAddModel={onAddModel}
          canManage={canManage}
        />
      </CardContent>
    </Card>
  );
};

const UserModelAssignmentView: React.FC = () => {
  const [isAvailableModelsCollapsed, setIsAvailableModelsCollapsed] = useState(true);
  const { user } = useAuth();
  const { data: models = [], isLoading: modelsLoading } = useModels();
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: assignments = [], isLoading: assignmentsLoading } = useAllAssignments();
  const { data: userOrders = [], isLoading: userOrdersLoading } = useAllUserOrders();
  
  const { mutateAsync: assignModel } = useAssignModel();
  const { mutateAsync: removeModel } = useRemoveModel();
  const { mutateAsync: moveModel } = useMoveModel();
  const { mutateAsync: updateUserOrders } = useUpdateUserOrders();
  const { mutateAsync: updateAssignmentOrder } = useUpdateAssignmentOrder();

  const canManage = !!user;

  // Group assignments by user and get assigned models
  const { assignedModelsByUser, availableModels, orderedUsersWithAssignments } = useMemo(() => {
    const assignedIds = new Set<string>();
    const assignedByUser: Record<string, Model[]> = {};

    // Group assignments by user
    const assignmentsByUser: Record<string, FanGainAssignment[]> = {};
    assignments.forEach(assignment => {
      if (!assignmentsByUser[assignment.user_id]) {
        assignmentsByUser[assignment.user_id] = [];
      }
      assignmentsByUser[assignment.user_id].push(assignment);
      assignedIds.add(assignment.model_id);
    });

    // Sort assignments by index and get models
    Object.keys(assignmentsByUser).forEach(user_id => {
      assignedByUser[user_id] = [];
      assignmentsByUser[user_id]
        .sort((a, b) => a.index - b.index)
        .forEach(assignment => {
          const model = models.find(m => m.id === assignment.model_id);
          if (model) {
            assignedByUser[user_id].push(model);
          }
        });
    });

    // Get available models (not assigned to anyone)
    const available = models.filter(model => 
      !assignedIds.has(model.id) && 
      model.groups.includes("GG Swap") && 
      model.account_type === 'Normal'
    );

    // Get users who have GG_SWAP_ROLE
    const usersWithRole = users.filter(user => 
      user.roles.includes(GG_SWAP_ROLE)
    );

    // Create a map of user orders for quick lookup
    const userOrderMap = new Map();
    userOrders.forEach(order => {
      userOrderMap.set(order.user_id, order.order_index);
    });

    // Sort users by their order_index, putting users without order at the end
    const orderedUsers = usersWithRole.sort((a, b) => {
      const aOrder = userOrderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = userOrderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    return {
      assignedModelsByUser: assignedByUser,
      availableModels: available,
      orderedUsersWithAssignments: orderedUsers
    };
  }, [models, users, assignments, userOrders]);

  const handleAddModel = async (model_id: string, user_id: string) => {
    if (!user) return;

    try {
      await assignModel({
        user_id,
        model_id
      });
      toast.success('Model assigned successfully');
    } catch (error) {
      console.error('Error assigning model:', error);
      toast.error('Failed to assign model');
    }
  };

  const handleRemoveModel = async (model_id: string, user_id: string) => {
    if (!user) return;

    try {
      await removeModel({
        user_id,
        model_id
      });
      toast.success('Model removed successfully');
    } catch (error) {
      console.error('Error removing model:', error);
      toast.error('Failed to remove model');
    }
  };

  const handleReorderUsers = async (draggedIndex: number, targetIndex: number) => {
    if (!user || draggedIndex === targetIndex) return;

    try {
      const reorderedUsers = [...orderedUsersWithAssignments];
      const [draggedUser] = reorderedUsers.splice(draggedIndex, 1);
      reorderedUsers.splice(targetIndex, 0, draggedUser);

      // Create new order array
      const newUserOrders = reorderedUsers.map((user, index) => ({
        user_id: user.id,
        orderIndex: index
      }));

      await updateUserOrders(newUserOrders);
      toast.success('User order updated successfully');
    } catch (error) {
      console.error('Error reordering users:', error);
      toast.error('Failed to update user order');
    }
  };

  const handleReorderModels = async (user_id: string, draggedIndex: number, targetIndex: number) => {
    if (!user || draggedIndex === targetIndex) return;

    try {
      const userAssignments = assignments.filter(a => a.user_id === user_id)
        .sort((a, b) => a.index - b.index);
      
      const reorderedAssignments = [...userAssignments];
      const [draggedAssignment] = reorderedAssignments.splice(draggedIndex, 1);
      reorderedAssignments.splice(targetIndex, 0, draggedAssignment);

      const assignmentIds = reorderedAssignments.map(a => a.id);
      await updateAssignmentOrder({ user_id, assignmentIds });
      toast.success('Model order updated successfully');
    } catch (error) {
      console.error('Error reordering models:', error);
      toast.error('Failed to update model order');
    }
  };

  const handleDrop = async (e: React.DragEvent, targetuser_id: string) => {
    e.preventDefault();
    
    if (!user) return;

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { type, model_id, sourceuser_id } = data;
      
      // Only handle model drops here, user drops are handled in UserCard
      if (type !== 'model' || !model_id) return;

      if (sourceuser_id && sourceuser_id !== targetuser_id) {
        // Move between users
        await moveModel({
          fromuser_id: sourceuser_id,
          touser_id: targetuser_id,
          model_id
        });
        toast.success('Model moved successfully');
      } else if (!sourceuser_id) {
        // Add from available models
        await handleAddModel(model_id, targetuser_id);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
      toast.error('Failed to move model');
    }
  };

  if (modelsLoading || usersLoading || assignmentsLoading || userOrdersLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-4 sm:space-y-4 px-2 sm:px-0">
      {/* Available Models Section */}
      {availableModels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Available Models ({availableModels.length})</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAvailableModelsCollapsed(!isAvailableModelsCollapsed)}
                className="h-6 w-6 p-0"
              >
                {isAvailableModelsCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          {!isAvailableModelsCollapsed && (
            <CardContent>
              <div className="max-h-48 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                  {availableModels.map(model => (
                    <div
                      key={model.id}
                      className="group flex items-center justify-between bg-white dark:bg-brand-dark border border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 shadow-sm hover:shadow-md transition-all cursor-move max-w-xs"
                      draggable={canManage}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          type: 'model',
                          model_id: model.id,
                          modelName: model.name
                        }));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                    >
                      <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
                        {canManage && <GripVertical className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />}
                        <span className="font-medium text-xs sm:text-sm truncate">{model.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* User Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {orderedUsersWithAssignments.map((user, index) => (
          <UserCard
            key={user.id}
            user={user}
            assignedModels={assignedModelsByUser[user.id] || []}
            availableModels={availableModels}
            onAddModel={handleAddModel}
            onRemoveModel={handleRemoveModel}
            onDrop={handleDrop}
            onReorderModels={handleReorderModels}
            canManage={canManage}
            index={index}
            onReorderUsers={handleReorderUsers}
          />
        ))}
      </div>

      {orderedUsersWithAssignments.length === 0 && (
        <div className="text-center py-12">
          <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-brand-dark dark:text-foreground mb-2">
            No assignments yet
          </h3>
          <p className="text-muted-foreground dark:text-muted-foreground mb-4">
            Users with GG Swap role will appear here once models are assigned to them
          </p>
        </div>
      )}
    </div>
  );
};

export default UserModelAssignmentView;